import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccessTimeOutlined,
  CalendarMonthOutlined,
  DirectionsCarOutlined,
  FilterAltOutlined,
  PaidOutlined,
  RefreshOutlined,
  RouteOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment-timezone";

import passengerApi from "Services/Passenger";
import BookingApi from "Services/Booking";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import "./HomePage.css";

const getViewTabs = (intl) => [
  { key: "all", label: intl.formatMessage({ id: "dashboard.bookings.tabs.all" }), icon: "all" },
  { key: "a_venir", label: intl.formatMessage({ id: "dashboard.bookings.tabs.upcoming" }), icon: "clock" },
  { key: "passe", label: intl.formatMessage({ id: "dashboard.bookings.tabs.past" }), icon: "calendar" },
];

const getStatusOptions = (intl) => [
  { value: "all", label: intl.formatMessage({ id: "dashboard.bookings.status.all" }) },
  { value: "pending", label: intl.formatMessage({ id: "dashboard.bookings.status.pending" }) },
  { value: "confirmed", label: intl.formatMessage({ id: "dashboard.bookings.status.confirmed" }) },
  { value: "cancelled", label: intl.formatMessage({ id: "dashboard.bookings.status.cancelled" }) },
  { value: "completed", label: intl.formatMessage({ id: "dashboard.bookings.status.completed" }) },
];

const STATUS_LABEL_KEYS = {
  pending: "dashboard.bookings.status.pending",
  confirmed: "dashboard.bookings.status.confirmed",
  cancelled: "dashboard.bookings.status.cancelled",
  completed: "dashboard.bookings.status.completed",
};

const statusToneMap = {
  pending: "is-active",
  confirmed: "is-active",
  cancelled: "is-past",
  completed: "is-past",
};

const getStatusLabel = (intl, status) => {
  const key = STATUS_LABEL_KEYS[status];
  return key ? intl.formatMessage({ id: key }) : formatEnumLabel(status || "Trajet");
};

const zoneForDate = (timezone) => timezone || "Africa/Lagos";

const getDateFns = (value, timezone) => {
  const date = value ? moment(value).tz(zoneForDate(timezone)) : null;
  return {
    dateLabel: date ? date.format("DD MMM") : "--",
    weekdayLabel: date ? date.format("dddd") : "--",
    timeLabel: date ? date.format("HH:mm") : "--:--",
  };
};

const toMoney = (value, currency = "XAF") => {
  if (value === null || value === undefined || value === "") {
    return `-- ${currency}`;
  }

  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return `${value} ${currency}`;
  }

  return `${new Intl.NumberFormat("fr-FR").format(amount)} ${currency}`;
};

const formatGridDate = (value, timezone) => {
  if (!value) {
    return "--";
  }

  const date = moment(value).tz(zoneForDate(timezone));
  return date.isValid() ? date.format("DD MMM YYYY HH:mm") : "--";
};

const formatEnumLabel = (value) => {
  if (!value) {
    return "--";
  }

  return String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/(^|\s)\S/g, (match) => match.toUpperCase());
};

const getTripWaypointNames = (trip) => {
  const sources = [
    trip?.ride_waypoints,
    trip?.rides?.ride_waypoints,
    trip?.ride?.ride_waypoints,
  ];

  const waypoints = sources.find((value) => Array.isArray(value)) || [];

  return waypoints.map((waypoint) =>
    safeJoin(
      waypoint?.places?.name,
      waypoint?.place?.name,
      waypoint?.place_name,
      waypoint?.name,
      waypoint?.label
    ) || "--"
  );
};

const getTripTableLocation = (value, fallback = "--") => {
  const text = safeJoin(value);
  return text && text !== "--" ? text : fallback;
};

const safeJoin = (...parts) => parts.filter(Boolean).join(" ");

const normalizeRide = (item, section, index, intl) => {
  const ride = item?.rides || item?.ride || {};
  const user = item?.users || item?.user || item?.passenger || {};
  const passengerFallback = intl.formatMessage({ id: "dashboard.bookings.fallback.passenger" });
  const departureFallback = intl.formatMessage({ id: "dashboard.bookings.fallback.departure" });
  const arrivalFallback = intl.formatMessage({ id: "dashboard.bookings.fallback.arrival" });

  const passengerName = safeJoin(user?.first_name, user?.last_name) || safeJoin(user?.firstName, user?.lastName) || passengerFallback;
  const userFullName = passengerName;
  const rideWaypoints = getTripWaypointNames(item);
  const vehicleNo =
    ride?.vehicle_no ||
    ride?.vehicles?.vehicle_no ||
    ride?.vehicle?.vehicle_no ||
    ride?.vehicleNumber ||
    ride?.plate_number ||
    "--";
  const vehicleName =
    ride?.vehicle_name ||
    ride?.vehicles?.vehicle_name ||
    ride?.vehicle?.vehicle_name ||
    ride?.vehicleModel ||
    ride?.vehicle_model ||
    "--";
  const bookingStatus = String(item?.status || item?.bookingStatus || "pending").toLowerCase();
  const normalizedBookingStatus = bookingStatus === "canceled" ? "cancelled" : bookingStatus;
  const statusLabel = getStatusLabel(intl, normalizedBookingStatus);
  const statusTone = statusToneMap[normalizedBookingStatus] || "is-active";
  const pickupAddress =
    item?.pickupAddress ||
    item?.pickup_address ||
    item?.ride_waypoints_bookings_pickup_waypoint_idToride_waypoints?.places?.name ||
    ride?.pickupAddress ||
    ride?.origin_place?.name ||
    ride?.origin_place?.label ||
    ride?.places_rides_origin_place_idToplaces?.name ||
    rideWaypoints[0] ||
    departureFallback;
  const stop1Address =
    item?.stop1_address ||
    ride?.stop1_address ||
    rideWaypoints[1] ||
    "--";
  const stop2Address =
    item?.stop2_address ||
    ride?.stop2_address ||
    rideWaypoints[2] ||
    "--";
  const dropAddress =
    item?.dropAddress ||
    item?.drop_address ||
    item?.ride_waypoints_bookings_dropoff_waypoint_idToride_waypoints?.places?.name ||
    ride?.dropAddress ||
    ride?.destination_place?.name ||
    ride?.destination_place?.label ||
    ride?.places_rides_destination_place_idToplaces?.name ||
    rideWaypoints[rideWaypoints.length - 1] ||
    arrivalFallback;
  const paymentMode = formatEnumLabel(
    item?.paymentMode ||
      item?.payment_mode ||
      ride?.paymentMode ||
      ride?.payment_mode ||
      item?.payment ||
      "--"
  );
  const totalSource =
    item?.estimatedFare ??
    item?.total_amount ??
    item?.totalAmount ??
    item?.fare ??
    item?.amount ??
    ride?.estimatedFare ??
    ride?.total_amount ??
    null;
  const totalValue = totalSource === null || totalSource === undefined || totalSource === "" ? null : Number(totalSource);
  const commissionSource =
    item?.earnedCommission ??
    item?.commission ??
    item?.service_fee ??
    ride?.earnedCommission ??
    ride?.service_fee ??
    null;
  const commissionValue =
    commissionSource === null || commissionSource === undefined || commissionSource === ""
      ? null
      : Number(commissionSource);

  return {
    raw: item,
    ride,
    id: item?.id || item?._id || item?.bookingId || item?.ride_id || item?.created_at || `${section}-${index}`,
    bookingId: item?.bookingId || item?.id || item?._id || item?.booking_id || item?.booking_number || "-",
    rideId: item?.ride_id || ride?.id || item?.rideId || "-",
    passengerName,
    userFullName,
    vehicleNo,
    vehicleName,
    phone: user?.phone || user?.mobileNumber || item?.phone || "-",
    paymentMode,
    originLabel: pickupAddress || departureFallback,
    destinationLabel: dropAddress || arrivalFallback,
    pickupPlace: getTripTableLocation(pickupAddress),
    stop1Place: getTripTableLocation(stop1Address),
    stop2Place: getTripTableLocation(stop2Address),
    dropLocation: getTripTableLocation(dropAddress),
    requestDetails: passengerName,
    requestedAt: item?.requestedAt || item?.created_at || item?.createdAt || ride?.departure_at || null,
    datedOn: formatGridDate(item?.requestedAt || item?.created_at || item?.createdAt || ride?.departure_at, item?.timezone),
    departureAt: item?.requestedAt || item?.created_at || item?.createdAt || ride?.departure_at || null,
    arrivalAt: ride?.estimated_arrival_at || item?.estimated_arrival_at || null,
    totalAmount: totalValue,
    totalDisplay: toMoney(totalValue, item?.currency || ride?.currency || "XAF"),
    serviceFee: commissionValue,
    commission: commissionValue,
    commissionDisplay: toMoney(commissionValue, item?.currency || ride?.currency || "XAF"),
    currency: item?.currency || ride?.currency || "XAF",
    seatsBooked: item?.seats_booked ?? 0,
    totalSeats: ride?.total_seats ?? null,
    availableSeats: ride?.available_seats ?? null,
    bookingStatus: normalizedBookingStatus,
    statusLabel,
    statusTone,
    statusBackground: normalizedBookingStatus === "completed" ? "#00c292" : normalizedBookingStatus === "cancelled" ? "#f8312fd6" : "#2d7ea8",
    rideStatus: String(ride?.status || "-").toLowerCase(),
    createdAt: item?.created_at || null,
    confirmedAt: item?.confirmed_at || null,
    cancellationReason: item?.cancellation_reason || "",
    driverId: ride?.driver_id || "-",
    section,
    price:totalValue/ride?.total_seats ?? null,
    priceDisplay: toMoney(totalValue/ride?.total_seats, item?.currency || ride?.currency || "XAF"),
  };
};

const normalizeList = (list, section, intl) => {
  if (!Array.isArray(list)) return [];
  return list.map((item, index) => normalizeRide(item, section, index, intl));
};

const matchesSearch = (trip, term) => {
  if (!term) return true;
  const haystack = [
    trip?.bookingId,
    trip?.rideId,
    trip?.passengerName,
    trip?.userFullName,
    trip?.phone,
    trip?.originLabel,
    trip?.destinationLabel,
    trip?.pickupPlace,
    trip?.stop1Place,
    trip?.stop2Place,
    trip?.dropLocation,
    trip?.bookingStatus,
    trip?.statusLabel,
    trip?.rideStatus,
    trip?.paymentMode,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(term.toLowerCase());
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: "grid", gap: 0.35 }}>
    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ color: "#10233f", wordBreak: "break-word" }}>
      {value || "--"}
    </Typography>
  </Box>
);

const TripDetailsDialog = ({ open, trip, timezone, onClose }) => {
  const intl = useIntl();

  if (!trip) return null;

  const departure = getDateFns(trip?.departureAt, timezone);
  const arrival = trip?.arrivalAt ? getDateFns(trip.arrivalAt, timezone) : null;
  const bookingStatusLabel = trip?.statusLabel || getStatusLabel(intl, trip?.bookingStatus) || trip?.bookingStatus || "--";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#10233f" }}>
              {intl.formatMessage({ id: "dashboard.bookings.details.title" })}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b"}}>
               {trip?.originLabel} - {trip?.destinationLabel}
            </Typography>
          </Box>
          <Chip
            label={bookingStatusLabel}
            color={trip?.bookingStatus === "cancelled" ? "error" : trip?.bookingStatus === "completed" ? "success" : "primary"}
            variant="outlined"
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#f8fafc" }}>
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid rgba(15, 39, 72, 0.08)",
              background: "#fff",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
              <Box>
                <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.16em" }}>
                  {intl.formatMessage({ id: "dashboard.bookings.details.passenger" })}
                </Typography>
                <Typography variant="h6" sx={{ color: "#10233f" }}>
                  {trip?.passengerName}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {trip?.phone}
                </Typography>
              </Box>

              <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.16em" }}>
                  {intl.formatMessage({ id: "dashboard.bookings.details.amount" })}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#0f6a3b" }}>
                  {toMoney(trip?.totalAmount, trip?.currency)}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {intl.formatMessage(
                    { id: "dashboard.bookings.details.serviceFee" },
                    { value: toMoney(trip?.serviceFee, trip?.currency) }
                  )}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: "1px solid rgba(15, 39, 72, 0.08)",
                  background: "#fff",
                  height: "100%",
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 900, color: "#10233f" }}>
                  {intl.formatMessage({ id: "dashboard.bookings.details.bookingSection.title" })}
                </Typography>
                <Stack spacing={2}>

                  <DetailRow label={intl.formatMessage({ id: "dashboard.bookings.details.bookingSection.status" })} value={bookingStatusLabel} />
                  <DetailRow
                    label={intl.formatMessage({ id: "dashboard.bookings.details.bookingSection.createdAt" })}
                    value={`${departure.dateLabel} ${departure.weekdayLabel} a ${departure.timeLabel}`}
                  />
                  <DetailRow
                    label={intl.formatMessage({ id: "dashboard.bookings.details.bookingSection.confirmedAt" })}
                    value={
                      trip?.confirmedAt
                        ? moment(trip.confirmedAt).tz(zoneForDate(timezone)).format("DD MMM YYYY HH:mm")
                        : "--"
                    }
                  />
                  <DetailRow
                    label={intl.formatMessage({ id: "dashboard.bookings.details.bookingSection.seats" })}
                    value={`${trip?.seatsBooked}/${trip?.totalSeats ?? "--"}`}
                  />

                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: "1px solid rgba(15, 39, 72, 0.08)",
                  background: "#fff",
                  height: "100%",
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 900, color: "#10233f" }}>
                  {intl.formatMessage({ id: "dashboard.bookings.details.tripSection.title" })}
                </Typography>
                <Stack spacing={2}>

                  <DetailRow label={intl.formatMessage({ id: "dashboard.bookings.details.tripSection.status" })} value={trip?.rideStatus} />
                  <DetailRow
                    label={intl.formatMessage({ id: "dashboard.bookings.details.tripSection.departure" })}
                    value={`${departure.dateLabel} ${departure.weekdayLabel} a ${departure.timeLabel}`}
                  />
                  <DetailRow
                    label={intl.formatMessage({ id: "dashboard.bookings.details.tripSection.estimatedArrival" })}
                    value={arrival ? `${arrival.dateLabel} ${arrival.weekdayLabel} a ${arrival.timeLabel}` : "--"}
                  />
                  <DetailRow label={intl.formatMessage({ id: "dashboard.bookings.details.tripSection.origin" })} value={trip?.originLabel} />
                  <DetailRow label={intl.formatMessage({ id: "dashboard.bookings.details.tripSection.destination" })} value={trip?.destinationLabel} />
                  <DetailRow
                    label={intl.formatMessage({ id: "dashboard.bookings.details.tripSection.cancellationReason" })}
                    value={trip?.cancellationReason || "--"}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {intl.formatMessage({ id: "dashboard.bookings.details.close" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TripsTable = ({ rows, timezone, onOpenDetails, onOpenTicket, ticketLoadingId, loading }) => {
  const intl = useIntl();
  const [pageSize, setPageSize] = useState(5);
  const renderTextCell = useCallback(
    (value) => (
      <Typography variant="body2" noWrap title={value ?? "--"} sx={{ width: "100%" }}>
        {value ?? "--"}
      </Typography>
    ),
    []
  );

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.sno" }),
        minWidth: 70,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => renderTextCell(params?.row?.sno),
      },
      {
        field: "pickupPlace",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.pickedUp" }),
        minWidth: 180,
        flex: 1.2,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.pickupPlace),
      },

      {
        field: "dropLocation",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.dropLocation" }),
        minWidth: 180,
        flex: 1.2,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.dropLocation),
      },


      {
        field: "status",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.status" }),
        minWidth: 150,
        flex: 1,
        sortable: true,
        renderCell: (params) => (
          <span className={`tc-status-pill tc-status-pill--${params?.row?.statusTone || "is-active"}`}>
            {params?.row?.statusLabel || "--"}
          </span>
        ),
      },
      {
        field: "paymentMode",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.paymentMode" }),
        minWidth: 150,
        flex: 1,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.paymentMode),
      },
      {
        field: "priceDisplay",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.price" }),
        minWidth: 120,
        flex: 0.9,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.priceDisplay),
      },
      {
        field: "totalSeats",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.seats" }),
        minWidth: 160,
        flex: 1,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.totalSeats),
      },
      {
        field: "totalDisplay",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.total" }),
        minWidth: 120,
        flex: 0.9,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.totalDisplay),
      },
      {
        field: "datedOn",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.datedOn" }),
        minWidth: 150,
        flex: 1,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.datedOn || formatGridDate(params?.row?.requestedAt, timezone)),
      },

      {
        field: "ticket",
        headerName: intl.formatMessage({ id: "dashboard.bookings.table.columns.ticket" }),
        minWidth: 160,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Button
            type="button"
            variant="outlined"
            size="small"
            disabled={ticketLoadingId === params?.row?.id}
            onClick={() => onOpenTicket(params?.row)}
            sx={{
              minWidth: 122,
              height: 34,
              px: 1.75,
              borderRadius: "10px",
              borderColor: "#ff9800",
              background: "#ffffff",
              color: "#ff9800",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              boxShadow: "none",
              lineHeight: 1,
              "&:hover": {
                borderColor: "#ef8b00",
                background: "#fff8ef",
              },
              "&.Mui-disabled": {
                borderColor: "rgba(255, 152, 0, 0.35)",
                color: "rgba(255, 152, 0, 0.5)",
                background: "#ffffff",
                boxShadow: "none",
              },
            }}
          >
            {ticketLoadingId === params?.row?.id
              ? intl.formatMessage({ id: "dashboard.bookings.table.ticketButton.loading" })
              : intl.formatMessage({ id: "dashboard.bookings.table.ticketButton.access" })}
          </Button>
        ),
      },
    ],
    [intl, onOpenDetails, onOpenTicket, renderTextCell, ticketLoadingId, timezone]
  );

  return (
    <Paper
      elevation={0}
      className="tc-table-card"
      sx={{
        borderRadius: 4,
        border: "1px solid rgba(15, 39, 72, 0.08)",
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        loading={loading}
        disableSelectionOnClick
        disableColumnMenu
        density="compact"
        pagination
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 25, 50]}
        rowHeight={60}
        headerHeight={56}
        getRowId={(row) => row.id}
        localeText={{ noRowsLabel: intl.formatMessage({ id: "dashboard.bookings.table.noRows" }) }}
        className="tc-data-grid-table"
      />
    </Paper>
  );
};

const StatCard = ({ label, value, helper, icon }) => (
  <Paper
    elevation={0}
    className="tc-summary-card"
    sx={{
      borderRadius: 3,
      border: "1px solid rgba(15, 39, 72, 0.08)",
      background: "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(243,248,251,0.92))",
      p: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "14px",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(180deg, #0f6a3b, #0a532e)",
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, letterSpacing: "0.04em" }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 900, color: "#10233f", lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
        {helper}
      </Typography>
    </Box>
  </Paper>
);

export default function TrajetClient() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timezone = useSelector((state) => state?.timezone?.timezone);
  const passengerId = useSelector((state) => state?.AuthReducer?.userid || localStorage.getItem("userid") || "");

  const viewTabs = useMemo(() => getViewTabs(intl), [intl]);
  const statusOptions = useMemo(() => getStatusOptions(intl), [intl]);

  const [selectedView, setSelectedView] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [upcomingTrips, setUpcomingTrips] = useState([]);
  const [pastTrips, setPastTrips] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [ticketLoadingId, setTicketLoadingId] = useState("");

  const loadPassengerRides = useCallback(async () => {
    if (!passengerId) {
      setUpcomingTrips([]);
      setPastTrips([]);
      setLoadError(intl.formatMessage({ id: "dashboard.bookings.errors.missingUserId" }));
      return;
    }

    setIsLoading(true);
    setLoadError("");
    dispatch(showLoader(intl.formatMessage({ id: "dashboard.bookings.loaders.loadingTrips" })));

    try {
      const params = { id: passengerId };
      if (selectedDate) params.date = selectedDate;
      if (selectedStatus !== "all") params.status = selectedStatus;

      const res = await passengerApi.getPassengerRides(params);
      if (res?.data?.status === true) {
        const aVenir = normalizeList(res?.data?.data?.a_venir, "a_venir", intl);
        const passe = normalizeList(res?.data?.data?.passe, "passe", intl);
        setUpcomingTrips(aVenir);
        setPastTrips(passe);
        setLoadedAt(new Date());
      } else {
        setUpcomingTrips([]);
        setPastTrips([]);
        setLoadError(res?.data?.message || intl.formatMessage({ id: "dashboard.bookings.errors.noTripsFound" }));
      }
    } catch (error) {
      setUpcomingTrips([]);
      setPastTrips([]);
      const message = error?.response?.data?.message || intl.formatMessage({ id: "dashboard.bookings.errors.loadError" });
      setLoadError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setIsLoading(false);
      dispatch(hideLoader());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, passengerId, selectedDate, selectedStatus, intl]);

  useEffect(() => {
    loadPassengerRides();
  }, [loadPassengerRides]);

  const visibleUpcoming = useMemo(
    () => upcomingTrips.filter((trip) => matchesSearch(trip, searchTerm)),
    [searchTerm, upcomingTrips]
  );
  const visiblePast = useMemo(
    () => pastTrips.filter((trip) => matchesSearch(trip, searchTerm)),
    [pastTrips, searchTerm]
  );

  const visibleTrips = useMemo(() => {
    if (selectedView === "a_venir") {
      return { upcoming: visibleUpcoming, past: [] };
    }

    if (selectedView === "passe") {
      return { upcoming: [], past: visiblePast };
    }

    return { upcoming: visibleUpcoming, past: visiblePast };
  }, [selectedView, visiblePast, visibleUpcoming]);

  const totalTrips = upcomingTrips.length + pastTrips.length;
  const filteredTripsCount = visibleTrips.upcoming.length + visibleTrips.past.length;
  const pendingCount = [...upcomingTrips, ...pastTrips].filter((trip) => trip.bookingStatus === "pending").length;
  const confirmedCount = [...upcomingTrips, ...pastTrips].filter((trip) => trip.bookingStatus === "confirmed").length;
  const completedCount = [...upcomingTrips, ...pastTrips].filter((trip) => trip.bookingStatus === "completed").length;
  const tableRows = useMemo(() => {
    const combined = [...visibleTrips.upcoming, ...visibleTrips.past];
    const seen = new Set();

    return combined
      .filter((trip) => {
        if (seen.has(trip.id)) {
          return false;
        }

        seen.add(trip.id);
        return true;
      })
      .sort((a, b) => {
        const aTime = moment(a?.requestedAt || a?.createdAt || a?.departureAt || 0).valueOf();
        const bTime = moment(b?.requestedAt || b?.createdAt || b?.departureAt || 0).valueOf();
        return bTime - aTime;
      })
      .map((trip, index) => ({
        ...trip,
        sno: index + 1,
      }));
  }, [visibleTrips.past, visibleTrips.upcoming]);

  const activeStatusLabel =
    statusOptions.find((option) => option.value === selectedStatus)?.label ||
    intl.formatMessage({ id: "dashboard.bookings.status.all" });

  const openDetails = useCallback((trip) => {
    setSelectedTrip(trip);
    setDetailsOpen(true);
  }, []);

  const closeDetails = useCallback(() => {
    setDetailsOpen(false);
  }, []);

  const openBookingTicket = useCallback(
    async (trip) => {
      const bookingId = trip?.bookingId;

      if (!bookingId || bookingId === "-") {
        dispatch(
          showSnackbar({
            message: intl.formatMessage({ id: "dashboard.bookings.errors.ticketMissingBooking" }),
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
        return;
      }

      setTicketLoadingId(trip.id);
      dispatch(showLoader(intl.formatMessage({ id: "dashboard.bookings.loaders.loadingTicket" })));

      try {
        const response = await BookingApi.getBookingById(bookingId);
        const bookingData = response?.data?.data ?? response?.data ?? null;

        if (!bookingData) {
          throw new Error("Booking introuvable.");
        }

        const rideData =
          bookingData?.ride ||
          bookingData?.rides ||
          bookingData?.rides?.[0] ||
          trip?.ride ||
          trip?.raw?.ride ||
          trip?.raw?.rides ||
          trip?.raw ||
          trip;

        const passengerData = bookingData?.passenger || bookingData?.user || bookingData?.users || null;
        const passengerCount = Number(bookingData?.seats_booked ?? trip?.seatsBooked ?? 1);
        const passengerEmail =
          passengerData?.email ||
          bookingData?.email ||
          bookingData?.user_email ||
          localStorage.getItem("email") ||
          "";

        const payload = {
          booking: bookingData,
          ride: rideData,
          passengerCount,
          passengerEmail,
        };

        sessionStorage.setItem("bookingConfirmationPayload", JSON.stringify(payload));
        navigate("/booking-confirmation", { state: payload });
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || intl.formatMessage({ id: "dashboard.bookings.errors.ticketLoadFailed" });
        dispatch(
          showSnackbar({
            message,
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      } finally {
        setTicketLoadingId("");
        dispatch(hideLoader());
      }
    },
    [dispatch, navigate, intl]
  );

  return (
    <>
      <Box className="tc-content" sx={{ display: "grid", gap: 2.25, pb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2.25, md: 3 },
            border: "1px solid rgba(15, 39, 72, 0.08)",
            background: "linear-gradient(135deg, rgb(19 145 83 / 97%) 0%, rgb(25 103 16 / 98%) 100%)",
            color: "#fff",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: "auto -20px -30px auto",
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05, mt: 0.5 }}>
                {intl.formatMessage({ id: "dashboard.bookings.hero.title" })}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, maxWidth: 760, color: "rgba(255,255,255,0.88)" }}>
                {intl.formatMessage({ id: "dashboard.bookings.hero.subtitle" })}
              </Typography>
            </Box>            <Button
              type="button"
              variant="contained"
              onClick={() => navigate("/trajet")}
              sx={{
                minWidth: { xs: "100%", md: 210 },
                minHeight: 34,
                borderRadius: 3,
                px: 3,
                py: 2,
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(10px)",
                textTransform: "none",
                fontSize: 18,
                fontWeight: 900,
                boxShadow: "none",
                "&:hover": {
                  background: "rgba(255,255,255,0.18)",
                  boxShadow: "none",
                },
              }}
            >
              {intl.formatMessage({ id: "dashboard.bookings.hero.reserve" })}
            </Button>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, md: 2.5 },
            border: "1px solid rgba(15, 39, 72, 0.08)",
            background: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", xl: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", xl: "center" }}
            justifyContent="space-between"
          >
            <Box className="tc-trip-tabs" sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>
              {viewTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`tc-trip-tab ${selectedView === tab.key ? "is-active" : ""}`}
                  onClick={() => setSelectedView(tab.key)}
                >
                  <span className="tc-trip-tab-icon">
                    {tab.icon === "clock" ? (
                      <AccessTimeOutlined fontSize="small" />
                    ) : tab.icon === "calendar" ? (
                      <CalendarMonthOutlined fontSize="small" />
                    ) : (
                      <RouteOutlined fontSize="small" />
                    )}
                  </span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </Box>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ flex: 1, justifyContent: "flex-end" }}
            >
              <TextField
                type="date"
                size="small"
                label={intl.formatMessage({ id: "dashboard.bookings.filters.date" })}
                InputLabelProps={{ shrink: true }}
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                sx={{ minWidth: { xs: "100%", md: 180 } }}
              />

              <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 200 } }}>
                <InputLabel id="trajet-status-label">{intl.formatMessage({ id: "dashboard.bookings.filters.status" })}</InputLabel>
                <Select
                  labelId="trajet-status-label"
                  label={intl.formatMessage({ id: "dashboard.bookings.filters.status" })}
                  value={selectedStatus}
                  onChange={(event) => setSelectedStatus(event.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder={intl.formatMessage({ id: "dashboard.bookings.filters.searchPlaceholder" })}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ color: "#90a4b8" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: "100%", md: 260 } }}
              />

              <Button
                variant="outlined"
                startIcon={<FilterAltOutlined />}
                onClick={loadPassengerRides}
                sx={{ minHeight: 40, whiteSpace: "nowrap" }}
              >
                {intl.formatMessage({ id: "dashboard.bookings.filters.filter" })}
              </Button>

              <Button
                variant="contained"
                startIcon={<RefreshOutlined />}
                onClick={loadPassengerRides}
                sx={{
                  minHeight: 40,
                  whiteSpace: "nowrap",
                  background: "linear-gradient(180deg, #0f6a3b, #0a532e)",
                }}
              >
                {intl.formatMessage({ id: "dashboard.bookings.filters.refresh" })}
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
              gap: 1.5,
              mt: 2.25,
            }}
          > 
            <StatCard
              label={intl.formatMessage({ id: "dashboard.bookings.stats.total.label" })}
              value={totalTrips}
              helper={intl.formatMessage({ id: "dashboard.bookings.stats.total.helper" }, { count: filteredTripsCount })}
              icon={<DirectionsCarOutlined />}
            />
            <StatCard
              label={intl.formatMessage({ id: "dashboard.bookings.stats.upcoming.label" })}
              value={upcomingTrips.length}
              helper={intl.formatMessage({ id: "dashboard.bookings.stats.upcoming.helper" })}
              icon={<RouteOutlined />}
            />
            <StatCard
              label={intl.formatMessage({ id: "dashboard.bookings.stats.past.label" })}
              value={pastTrips.length}
              helper={intl.formatMessage({ id: "dashboard.bookings.stats.past.helper" })}
              icon={<AccessTimeOutlined />}
            />
            <StatCard
              label={intl.formatMessage({ id: "dashboard.bookings.stats.status.label" })}
              value={selectedStatus === "all" ? intl.formatMessage({ id: "dashboard.bookings.stats.status.all" }) : activeStatusLabel}
              helper={intl.formatMessage(
                { id: "dashboard.bookings.stats.status.helper" },
                { pending: pendingCount, confirmed: confirmedCount, completed: completedCount }
              )}
              icon={<PaidOutlined />}
            />
          </Box>
        </Paper>

        {loadError ? <Alert severity="error">{loadError}</Alert> : null}

        <TripsTable
          rows={tableRows}
          timezone={timezone}
          onOpenDetails={openDetails}
          onOpenTicket={openBookingTicket}
          ticketLoadingId={ticketLoadingId}
          loading={isLoading}
        />

        <TripDetailsDialog open={detailsOpen} trip={selectedTrip} timezone={timezone} onClose={closeDetails} />
      </Box>
    </>
  );
}